/**
 * Firebase initialization. Only Google sign-in is enabled on web v1.
 *
 * Note: every value here is a *public* identifier (apiKey is not a secret —
 * it identifies the project. Auth is enforced by Firebase rules + backend
 * verification of the ID token).
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  setPersistence,
  signInWithRedirect,
  signOut as fbSignOut,
  onAuthStateChanged,
  type Auth,
  type User as FbUser,
} from 'firebase/auth';

interface FirebaseClientConfig {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  appId?: string;
}

function readConfig(): FirebaseClientConfig {
  // Read on every call so tests can stub via vi.stubEnv().
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export function isFirebaseConfigured(): boolean {
  const cfg = readConfig();
  return Boolean(cfg.apiKey && cfg.authDomain && cfg.projectId && cfg.appId);
}

function ensureApp(): FirebaseApp {
  if (_app) return _app;
  const cfg = readConfig();
  if (!cfg.apiKey || !cfg.authDomain || !cfg.projectId || !cfg.appId) {
    throw new Error('Firebase is not configured. Set VITE_FIREBASE_* env vars.');
  }
  _app =
    getApps()[0] ??
    initializeApp({
      apiKey: cfg.apiKey,
      authDomain: cfg.authDomain,
      projectId: cfg.projectId,
      appId: cfg.appId,
    });
  return _app;
}

export function getFbAuth(): Auth {
  if (_auth) return _auth;
  const a = getAuth(ensureApp());
  // browserLocalPersistence is the default but pin it explicitly so the
  // redirect round-trip still finds the auth state on the second page load.
  void setPersistence(a, browserLocalPersistence).catch(() => {
    /* private-mode storage falls back to in-memory; ignore */
  });
  _auth = a;
  return _auth;
}

/**
 * Starts a Google sign-in via REDIRECT (not popup). Popup-mode fails on
 * browsers that block third-party cookies on the popup-handler origin
 * (Safari default, Brave, recent Chrome with Privacy Sandbox), surfacing
 * a generic `auth/internal-error`. Redirect mode posts the result back
 * via a query-string on the same origin and is robust to the cookie block.
 *
 * After the user picks an account on Google's page, the user returns to
 * the SPA at `/__/auth/callback` (Firebase intercepts) and the SDK fires
 * `onAuthStateChanged` automatically. We also call `consumeRedirectResult`
 * on every cold start to surface any error from the previous round-trip.
 */
export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithRedirect(getFbAuth(), provider);
}

/**
 * Read + clear the redirect-flow result on cold start. Returns the user
 * if the previous redirect succeeded, the error if it failed, or null if
 * there was no pending redirect (a fresh page load).
 */
export async function consumeRedirectResult(): Promise<{
  user: FbUser | null;
  error: Error | null;
}> {
  try {
    const result = await getRedirectResult(getFbAuth());
    return { user: result?.user ?? null, error: null };
  } catch (e) {
    return { user: null, error: e instanceof Error ? e : new Error(String(e)) };
  }
}

export function signOut(): Promise<void> {
  return fbSignOut(getFbAuth());
}

export function subscribeAuth(cb: (user: FbUser | null) => void): () => void {
  return onAuthStateChanged(getFbAuth(), cb);
}

export type { FbUser };
